import { SupportChatCategory, SupportChatPriority } from "../../../common/enums";
import {
  AI_DEFAULT_MAX_IMAGE_BYTES,
  AI_DEFAULT_MAX_IMAGES_PER_MESSAGE,
  AI_DEFAULT_MAX_MESSAGE_CHARS,
  AiMessageRole,
  AiStreamEvent,
  IAiAssistantStatus,
  IAiConversation,
  IAiConversationListItem,
  IAiMessage,
  IAiMessageImage,
  IAiSupportDraft,
  IAiSupportTicket,
  IAiUploadedImage,
} from "../models/aiAssistantModels";

/**
 * Turns the AiAssistant API's raw JSON into the page's models. Defensive on purpose: queries are
 * camelCased by useApiQuery, mutation answers and stream frames are not, an endpoint may answer
 * with a JSON string (Ok(JsonSerializer.Serialize(x))), and enums may come as names or numbers.
 */

type RawObject = Record<string, unknown>;

const parseJsonIfString = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const asObject = (value: unknown): RawObject | null => {
  const parsed = parseJsonIfString(value);
  return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
    ? (parsed as RawObject)
    : null;
};

/** `obj.someKey ?? obj.SomeKey` */
const read = (obj: RawObject | null, key: string): unknown => {
  if (!obj) return undefined;
  if (obj[key] !== undefined) return obj[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[pascal];
};

const readString = (obj: RawObject | null, key: string, fallback = ""): string => {
  const value = read(obj, key);
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return fallback;
};

const readNullableString = (obj: RawObject | null, key: string): string | null => {
  const value = read(obj, key);
  if (typeof value === "string" && value.length > 0) return value;
  if (typeof value === "number") return String(value);
  return null;
};

const readNumber = (obj: RawObject | null, key: string, fallback: number): number => {
  const value = read(obj, key);
  const number = typeof value === "string" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : fallback;
};

const readNullableNumber = (obj: RawObject | null, key: string): number | null => {
  const value = read(obj, key);
  const number = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : null;
};

const readBoolean = (obj: RawObject | null, key: string, fallback = false): boolean => {
  const value = read(obj, key);
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
};

const readArray = (obj: RawObject | null, key: string): unknown[] => {
  const value = parseJsonIfString(read(obj, key));
  return Array.isArray(value) ? value : [];
};

/** An enum that may arrive as its numeric value or as its C# name ("Technical"). */
const parseEnum = <T extends number>(
  value: unknown,
  enumObject: Record<string, string | number>,
  fallback: T,
): T => {
  if (typeof value === "number" && enumObject[value] !== undefined) {
    return value as T;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed) && enumObject[Number(trimmed)] !== undefined) {
      return Number(trimmed) as T;
    }
    const match = Object.keys(enumObject).find(
      (key) => isNaN(Number(key)) && key.toLowerCase() === trimmed.toLowerCase(),
    );
    if (match) {
      return enumObject[match] as T;
    }
  }
  return fallback;
};

export const parseSupportCategory = (value: unknown): SupportChatCategory =>
  parseEnum(value, SupportChatCategory as unknown as Record<string, string | number>, SupportChatCategory.Technical);

export const parseSupportPriority = (value: unknown): SupportChatPriority =>
  parseEnum(value, SupportChatPriority as unknown as Record<string, string | number>, SupportChatPriority.Medium);

export const normalizeStatus = (raw: unknown): IAiAssistantStatus => {
  const obj = asObject(raw);
  return {
    enabled: readBoolean(obj, "enabled"),
    usedPercent: Math.max(0, readNumber(obj, "usedPercent", 0)),
    limitReached: readBoolean(obj, "limitReached"),
    resetsOn: readNullableString(obj, "resetsOn"),
    maxImagesPerMessage: Math.max(0, readNumber(obj, "maxImagesPerMessage", AI_DEFAULT_MAX_IMAGES_PER_MESSAGE)),
    maxMessageChars: Math.max(1, readNumber(obj, "maxMessageChars", AI_DEFAULT_MAX_MESSAGE_CHARS)),
    maxImageBytes: Math.max(1, readNumber(obj, "maxImageBytes", AI_DEFAULT_MAX_IMAGE_BYTES)),
  };
};

export const normalizeConversationList = (raw: unknown): IAiConversationListItem[] => {
  const parsed = parseJsonIfString(raw);
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map(asObject)
    .filter((item): item is RawObject => !!item && !!readNullableString(item, "id"))
    .map((item) => ({
      id: readString(item, "id"),
      title: readString(item, "title"),
      lastMessageAt: readNullableString(item, "lastMessageAt"),
    }));
};

export const normalizeSupportDraft = (raw: unknown): IAiSupportDraft | null => {
  const obj = asObject(raw);
  if (!obj) return null;
  const title = readString(obj, "title");
  const content = readString(obj, "content");
  if (!title && !content) return null;
  return {
    title,
    content,
    category: parseSupportCategory(read(obj, "category")),
    priority: parseSupportPriority(read(obj, "priority")),
  };
};

/** A ticket without its id has nothing to link to. */
export const normalizeSupportTicket = (raw: unknown): IAiSupportTicket | null => {
  const obj = asObject(raw);
  const id = readNullableString(obj, "id");
  if (!obj || !id) return null;
  return { id, title: readString(obj, "title") };
};

const normalizeRole = (value: unknown): AiMessageRole => {
  if (value === 0 || value === "0") return "user";
  if (typeof value === "string" && value.toLowerCase() === "user") return "user";
  return "assistant";
};

const normalizeImages = (raw: unknown[]): IAiMessageImage[] =>
  raw
    .map(asObject)
    .map((image) => ({ id: readString(image, "id"), url: readString(image, "url") }))
    .filter((image) => !!image.url);

export const normalizeMessage = (raw: unknown): IAiMessage | null => {
  const obj = asObject(raw);
  const id = readNullableNumber(obj, "id");
  if (!obj || id === null) return null;
  return {
    id,
    role: normalizeRole(read(obj, "role")),
    text: readString(obj, "text"),
    createdOn: readNullableString(obj, "createdOn"),
    isError: readBoolean(obj, "isError"),
    images: normalizeImages(readArray(obj, "images")),
    toolLabels: readArray(obj, "toolLabels").filter((label): label is string => typeof label === "string"),
    supportDraft: normalizeSupportDraft(read(obj, "supportDraft")),
    supportChatId: readNullableString(obj, "supportChatId"),
    supportTicket: normalizeSupportTicket(read(obj, "supportTicket")),
  };
};

export const normalizeConversation = (raw: unknown): IAiConversation | null => {
  const obj = asObject(raw);
  if (!obj) return null;
  return {
    id: readString(obj, "id"),
    title: readString(obj, "title"),
    isFull: readBoolean(obj, "isFull"),
    messages: readArray(obj, "messages")
      .map(normalizeMessage)
      .filter((message): message is IAiMessage => !!message),
  };
};

export const normalizeUploadedImage = (raw: unknown, fallbackFileName: string): IAiUploadedImage | null => {
  const obj = asObject(raw);
  const id = readNullableString(obj, "id");
  if (!obj || !id) return null;
  return {
    id,
    url: readString(obj, "url"),
    fileName: readString(obj, "fileName", fallbackFileName),
    size: readNumber(obj, "size", 0),
  };
};

/** The id of the ticket CreateSupportTicket made. */
export const readSupportChatId = (raw: unknown): string | null =>
  readNullableString(asObject(raw), "supportChatId");

/** `{ message, code }` of an error answer; null fields when the body has none. */
export const readErrorBody = (raw: unknown): { message: string | null; code: string | null } => {
  const obj = asObject(raw);
  if (!obj) {
    return { message: typeof raw === "string" && raw.trim() ? raw.trim() : null, code: null };
  }
  return { message: readNullableString(obj, "message"), code: readNullableString(obj, "code") };
};

/** One "data:" frame of the Send stream; null for anything that is not a known event. */
export const parseStreamEvent = (data: string): AiStreamEvent | null => {
  const obj = asObject(data);
  const type = readString(obj, "type");
  switch (type) {
    case "start":
      return {
        type,
        conversationId: readString(obj, "conversationId"),
        title: readString(obj, "title"),
        userMessageId: readNullableNumber(obj, "userMessageId"),
      };
    case "text":
      return { type, text: readString(obj, "text") };
    case "tool":
      return { type, name: readString(obj, "name"), label: readString(obj, "label") };
    case "supportDraft": {
      const draft = normalizeSupportDraft(read(obj, "draft"));
      return draft ? { type, messageId: readNullableNumber(obj, "messageId"), draft } : null;
    }
    case "supportTicket": {
      const ticket = normalizeSupportTicket(read(obj, "ticket"));
      return ticket ? { type, messageId: readNullableNumber(obj, "messageId"), ticket } : null;
    }
    case "done":
      return {
        type,
        messageId: readNullableNumber(obj, "messageId"),
        usedPercent: readNullableNumber(obj, "usedPercent"),
        limitReached: readBoolean(obj, "limitReached"),
        isFull: readBoolean(obj, "isFull"),
      };
    case "error":
      return { type, message: readString(obj, "message"), code: readNullableString(obj, "code") };
    default:
      return null;
  }
};

const BULGARIAN_MONTHS_GENITIVE = [
  "януари",
  "февруари",
  "март",
  "април",
  "май",
  "юни",
  "юли",
  "август",
  "септември",
  "октомври",
  "ноември",
  "декември",
];

/**
 * "1 ноември" for the budget reset. The reset is a UTC calendar date, so the date part is read
 * as written, not converted to the browser's zone (which could turn 1 November into 31 October).
 */
export const formatResetDate = (resetsOn: string | null, now: Date = new Date()): string => {
  const match = resetsOn ? /^(\d{4})-(\d{2})-(\d{2})/.exec(resetsOn) : null;
  if (match) {
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    if (month >= 0 && month < 12 && day > 0) {
      return `${day} ${BULGARIAN_MONTHS_GENITIVE[month]}`;
    }
  }
  const nextMonth = (now.getUTCMonth() + 1) % 12;
  return `1 ${BULGARIAN_MONTHS_GENITIVE[nextMonth]}`;
};

/** "12%" for the usage meter; a sliver of use reads "под 1%" rather than a misleading "0%". */
export const formatUsedPercent = (usedPercent: number): string => {
  if (usedPercent > 0 && usedPercent < 1) return "под 1%";
  return `${Math.min(100, Math.round(usedPercent))}%`;
};
